import pino, { type Logger as PinoLogger } from 'pino';
import { scrubLogPayload } from './LogScrubber';
import { getAppVersion } from '@shared/constants/appVersion';

const baseLogger: PinoLogger = pino({
  browser: { asObject: true },
  level:
    typeof import.meta !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV
      ? 'debug'
      : 'info',
  base: { app: 'nexxus-erp', version: getAppVersion() },
});

export interface LogContext {
  module: string;
  function?: string;
  tenant_id_hash?: string;
  payload?: Record<string, unknown>;
}

export class Logger {
  private static scrub(ctx: LogContext): LogContext {
    const payload = ctx.payload ? scrubLogPayload(ctx.payload) : undefined;
    return payload ? { ...ctx, payload } : ctx;
  }

  static info(event: string, ctx: LogContext): void {
    baseLogger.info({ event, ...this.scrub(ctx) });
  }
  static warn(event: string, ctx: LogContext): void {
    baseLogger.warn({ event, ...this.scrub(ctx) });
  }
  static error(event: string, ctx: LogContext, error?: Error): void {
    baseLogger.error({ event, ...this.scrub(ctx), err: error?.message, stack: error?.stack });
  }
  static debug(event: string, ctx: LogContext): void {
    baseLogger.debug({ event, ...this.scrub(ctx) });
  }
}

/** Export pratique pour usage simple : logger.info({ event, ... }). */
export const logger = {
  info(payload: Record<string, unknown>): void {
    const scrubbed = scrubLogPayload(payload);
    Logger.info(String(scrubbed['event'] ?? 'info'), {
      module: String(scrubbed['module'] ?? 'app'),
      payload: scrubbed,
    });
  },
  warn(payload: Record<string, unknown>): void {
    const scrubbed = scrubLogPayload(payload);
    Logger.warn(String(scrubbed['event'] ?? 'warn'), {
      module: String(scrubbed['module'] ?? 'app'),
      payload: scrubbed,
    });
  },
  error(payload: Record<string, unknown>): void {
    const scrubbed = scrubLogPayload(payload);
    Logger.error(String(scrubbed['event'] ?? 'error'), {
      module: String(scrubbed['module'] ?? 'app'),
      payload: scrubbed,
    });
  },
  debug(payload: Record<string, unknown>): void {
    const scrubbed = scrubLogPayload(payload);
    Logger.debug(String(scrubbed['event'] ?? 'debug'), {
      module: String(scrubbed['module'] ?? 'app'),
      payload: scrubbed,
    });
  },
};
